import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AuthService } from './auth.service';
import { Config } from 'src/app/config';
import { Observable } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { ServiceCallInfo } from '../models/common';
import { DataElement } from '../models/common'; 
import { SharedService } from 'src/app/shared/services/shared.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class ReferenceMappingService {

  private scaiServiceUrl: string;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private sharedService: SharedService,
    private translateService: TranslateService
  ) {
    
    this.scaiServiceUrl = Config.assetConfigData['scaiServiceUrl'];
  }

  getDocuments(catVersionId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.scaiServiceUrl}reference-mapping?catVersionId=${catVersionId}`, {
      headers: this.authService.getRequestHeaders()
    });
  }
  
  getExistingTexts(catId: number): Observable<string[]> {
    return this.http.get<string[]>(`${this.scaiServiceUrl}reference-mapping/existing-texts?catId=${catId}`, {
      headers: this.authService.getRequestHeaders()
    });
  }
  
  updateReferenceMapping(documentId: string, referenceText: string, catVersionId: number, id: number): Observable<any> {

    if (referenceText.length === 0) {
      const errorMsg = this.translateService.instant('translate.alert.referenceTextMissing');
      this.sharedService.alertInfo(errorMsg);  
      return new Observable();
    }
  
    return this.http.put(`${this.scaiServiceUrl}reference-mapping/${id}`, 
      { document_id: documentId, reference_text: referenceText, cat_version_id: catVersionId }, 
      { headers: this.authService.getRequestHeaders() }
    ).pipe(
      map((response) => {
        return response;
      }),
      catchError((err) => {
        const errorMsg = this.translateService.instant('translate.alert.referenceTextUpdateError');
        this.sharedService.alertInfo(errorMsg);  
        return this.handleError(err, `${this.scaiServiceUrl}reference-mapping/${id}`);
      })
    );
  }

  createReferenceMapping(documentId: string, docName: string, referenceText: string, catVersionId: number): Observable<any> {

    if (referenceText.length === 0) {
      const errorMsg = this.translateService.instant('translate.alert.referenceTextMissing');
      this.sharedService.alertInfo(errorMsg);  
      return new Observable();
    }
  
    return this.http.post(`${this.scaiServiceUrl}reference-mapping`, 
      { document_id: documentId, doc_name: docName, reference_text: referenceText, cat_version_id: catVersionId }, 
      { headers: this.authService.getRequestHeaders() }
    ).pipe(
      map((response) => {
        return response;
      }),
      catchError((err) => {
        const errorMsg = this.translateService.instant('translate.alert.referenceTextUpdateError');
        this.sharedService.alertInfo(errorMsg);  
        return this.handleError(err, `${this.scaiServiceUrl}reference-mapping`);
      })
    );
  }

  deleteReferenceMapping(id: number, catVersionId: number): Observable<any> {
    const params = new HttpParams()
      .set('cat_version_id', catVersionId.toString());
    return this.http.delete(`${this.scaiServiceUrl}reference-mapping/${id}`,
      { headers: this.authService.getRequestHeaders(), params }
    ).pipe(
      map((response) => response),
      catchError((err) => this.handleError(err, `${this.scaiServiceUrl}reference-mapping/${id}`))
    );
  }

  
  private handleError(error: any, url: string): Observable<never> {
    const info = new ServiceCallInfo(url, url, null);
    const errorMessage = error.error?.description || error.statusText || 'An unknown error occurred';

    return this.authService.handleError({ ...error, message: errorMessage }, info);
  }
}
